import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { canAccessMainTab } from "@/lib/maintabAccess";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/orgApi";
import {
  createEncounter, fetchPatients, fetchLocations, formToPayload, resourceToForm, patientNameOf,
  encounterNumberOf, clinicalStatusForSave, ENCOUNTER_CLASSES, ENCOUNTER_READ_ONLY_REASON,
  isEncounterReadOnly,
} from "@/lib/encounterApi";
import { encounterStatusLabel } from "@/lib/patientChart";
import { newEncounterFormState } from "@/lib/patientChartNewEncounter";
import EncounterReadOnlyHint from "@/components/encounter/EncounterReadOnlyHint";
import { ENC } from "@/constants/testIds";

// UI-only Type of Visit labels (FHIR Encounter.type conceptually).
// Values reuse existing form codes so the encounter payload/data model is unchanged.
// Formal FHIR terminology mapping is deferred.
const VISIT_TYPE_OPTIONS = [
  { code: "185349003", display: "New Visit" },
  { code: "390906007", display: "Follow-up Visit" },
  { code: "448337001", display: "Tele Visit" },
];

function Sel({ label, value, onChange, testId, options, placeholder, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-slate-700">{label} {required && <span className="text-rose-600">*</span>}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder || "Select"} /></SelectTrigger>
        <SelectContent position="popper" className="z-[200] max-h-96">
          {options.map((o) => (
            <SelectItem key={o.code} value={o.code}>{o.display}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function EncounterForm({ open, onClose, editing, onSaved, presetPatientId }) {
  const { user } = useAuth();
  const canReadPatients = canAccessMainTab(user?.maintabAccess, "patients");
  const [f, setF] = useState(() => newEncounterFormState());
  const [patients, setPatients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    Promise.all([
      canReadPatients ? fetchPatients() : Promise.resolve([]),
      fetchLocations(),
    ]).then(([pl, ll]) => {
      setPatients(pl);
      setLocations(ll);
      const base = editing ? resourceToForm(editing) : newEncounterFormState(presetPatientId);
      if (!base.locationId && ll.length === 1) base.locationId = ll[0].id;
      setF(base);
    });
  }, [open, editing, presetPatientId, canReadPatients]);

  const isVirtual = f.class === "VR";
  const encounterLocked = isEncounterReadOnly(editing);

  const submit = async () => {
    if (encounterLocked) return toast.info(ENCOUNTER_READ_ONLY_REASON);
    if (!f.patientId) return toast.error("Patient is required.");
    if (!isVirtual && !f.locationId) return toast.error("Location is required for a non-virtual encounter.");
    const payload = formToPayload(
      { ...f, status: clinicalStatusForSave(editing) },
      patients,
      locations,
    );
    setSaving(true);
    try {
      let created = null;
      if (editing?.id) {
        await api.put(`/encounters/${editing.id}`, payload);
        toast.success("Encounter updated.");
      } else {
        created = await createEncounter(payload);
        toast.success(`Encounter created — ${encounterNumberOf(created)}`);
      }
      onSaved?.(created);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : detail;
      toast.error(msg || "Failed to save encounter.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-visible" data-testid={ENC.form}>
        <DialogHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <DialogTitle>{editing ? "Edit Encounter" : "New Encounter"}</DialogTitle>
              <DialogDescription>FHIR R4 Encounter</DialogDescription>
            </div>
            {editing && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Encounter #</div>
                <span className="font-fhir text-sm text-teal-700">{encounterNumberOf(editing)}</span>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 py-2 sm:grid-cols-2">
          <Sel label="Patient" required value={f.patientId} onChange={set("patientId")} testId={ENC.fPatient}
            options={patients.map((p) => ({ code: p.id, display: patientNameOf(p) || p.id }))}
            placeholder="Select patient" />
          <Sel label={`Location ${isVirtual ? "(optional — virtual)" : ""}`} required={!isVirtual}
            value={f.locationId} onChange={set("locationId")} testId={ENC.fLocation}
            options={locations.map((l) => ({ code: l.id, display: l.name }))} placeholder="Select location" />
          <Sel label="Class" required value={f.class} onChange={set("class")} testId={ENC.fClass} options={ENCOUNTER_CLASSES} />
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Status</Label>
            <div
              data-testid={ENC.fStatus}
              className="flex h-9 items-center rounded-md border border-slate-200 bg-slate-50 px-3"
            >
              <Badge className="bg-slate-100 text-slate-800 hover:bg-slate-100">
                {encounterStatusLabel(clinicalStatusForSave(editing) || f.status)}
              </Badge>
            </div>
            <p className="text-xs text-slate-500">
              {editing
                ? "Clinical status is changed by Start Encounter and Close Encounter, not from this form."
                : "New encounters are Planned. Start Encounter from an Arrived appointment begins clinical care."}
            </p>
          </div>
          <Sel label="Type of Visit" value={f.type} onChange={set("type")} testId={ENC.fType} options={VISIT_TYPE_OPTIONS} placeholder="Select type of visit (optional)" />
          <div className="hidden sm:block" />
          <div className="space-y-1.5">
            <Label htmlFor="enc-start" className="text-sm text-slate-700">Period start</Label>
            <Input id="enc-start" type="datetime-local" data-testid={ENC.fStart} value={f.start}
              onChange={(e) => set("start")(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="enc-end" className="text-sm text-slate-700">Period end</Label>
            <Input id="enc-end" type="datetime-local" data-testid={ENC.fEnd} value={f.end}
              onChange={(e) => set("end")(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={ENC.cancelBtn}>Cancel</Button>
          <EncounterReadOnlyHint locked={encounterLocked}>
            <Button
              onClick={submit}
              disabled={saving || encounterLocked}
              data-testid={ENC.saveBtn}
              className="bg-teal-700 hover:bg-teal-800"
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Create encounter"}
            </Button>
          </EncounterReadOnlyHint>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
